import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import styled from '@emotion/styled';
import { BrowserRouter, Routes, Route, useSearchParams } from 'react-router-dom';
import AdminPage from './components/AdminPage';
import { keyframes } from '@emotion/react';

const UserStatus = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  padding: 0.5rem 1rem;
  background: #e8f5e9;
  border-radius: 20px;
  color: #2e7d32;
  font-size: 0.9rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  &::before {
    content: '•';
    color: #2e7d32;
    font-size: 1.5rem;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
  text-align: center;
`;

const Title = styled.h1`
  font-size: 2.5rem;
  color: #333;
  margin-bottom: 2rem;
`;

const DropzoneContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  margin-bottom: 2rem;

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr;
  }
`;

const DropBox = styled.div`
  border: 2px dashed #ccc;
  border-radius: 12px;
  padding: 2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  background: ${props => props.hasImage ? '#f8f9fa' : '#fff'};
  
  &:hover {
    border-color: #666;
  }

  h2 {
    margin-bottom: 1rem;
    color: #333;
  }

  p {
    color: #666;
    margin: 0;
  }
`;

const PreviewImage = styled.img`
  max-width: 100%;
  max-height: 300px;
  border-radius: 8px;
  margin-top: 1rem;
`;

const ResultContainer = styled.div`
  margin: 2rem auto;
  padding: 2rem;
  background-color: #fff;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  text-align: center;
  max-width: 90%;
`;

const ResultImage = styled.img`
  max-width: 100%;
  max-height: 70vh;
  border-radius: 8px;
  margin-top: 1rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  object-fit: contain;
`;

const ResultTitle = styled.h2`
  font-size: 1.8rem;
  color: #333;
  margin-bottom: 1rem;
`;

const ButtonContainer = styled.div`
  position: fixed;
  bottom: 2rem;
  left: 0;
  right: 0;
  text-align: center;
  background: linear-gradient(to top, rgba(248, 249, 250, 1) 50%, rgba(248, 249, 250, 0));
  padding: 2rem;
  z-index: 100;
`;

const LoginForm = styled.div`
  max-width: 400px;
  margin: 2rem auto;
  padding: 2rem;
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  text-align: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  margin: 1rem 0;
  border: 1px solid #ccc;
  border-radius: 6px;
  font-size: 1rem;
`;

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const pulse = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
`;

const ButtonSpinner = styled.div`
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top: 2px solid white;
  width: 16px;
  height: 16px;
  display: inline-block;
  animation: ${spin} 1s linear infinite;
  margin-right: 10px;
`;

const LoadingOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const Spinner = styled.div`
  border: 4px solid rgba(0, 123, 255, 0.1);
  border-radius: 50%;
  border-top: 4px solid #007bff;
  width: 50px;
  height: 50px;
  animation: ${spin} 1s linear infinite;
  margin-bottom: 20px;
`;

const LoadingText = styled.div`
  font-size: 1.2rem;
  color: #007bff;
  animation: ${pulse} 1.5s infinite ease-in-out;
  text-align: center;
  max-width: 80%;
  line-height: 1.5;
`;

const Button = styled.button`
  background: #007bff;
  color: white;
  border: none;
  padding: 1rem 2rem;
  font-size: 1.1rem;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  &:hover {
    background: #0056b3;
    transform: translateY(-2px);
    box-shadow: 0 6px 8px rgba(0, 0, 0, 0.15);
  }

  &:disabled {
    background: #007bff;
    opacity: 0.7;
    cursor: not-allowed;
    transform: none;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
`;

function ImageDropzone({ onDrop, title, image }) {
  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png']
    },
    onDrop
  });

  return (
    <DropBox {...getRootProps()} hasImage={!!image}>
      <input {...getInputProps()} />
      <h2>{title}</h2>
      {image ? (
        <PreviewImage src={URL.createObjectURL(image)} alt={title} />
      ) : (
        <p>Drag and drop an image here, or click to select</p>
      )}
    </DropBox>
  );
}

function AppContent() {
  // eslint-disable-next-line no-unused-vars
  const [searchParams, setSearchParams] = useSearchParams();
  const [homeImage, setHomeImage] = useState(null);
  const [inspirationImage, setInspirationImage] = useState(null);
  const [email, setEmail] = useState(() => localStorage.getItem('userEmail') || '');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [verifyingEmail, setVerifyingEmail] = useState('');
  const [loadingTextIndex, setLoadingTextIndex] = useState(0);
  const loadingIntervalRef = useRef(null);
  
  const loadingMessages = [
    "Applying design magic...",
    "Transforming your space...",
    "Almost ready to reveal...",
    "Adding designer touches...",
    "Reimagining your home...",
    "Creating your dream space...",
    "Blending styles perfectly...",
    "Enhancing your living area...",
    "Working design wonders...",
    "Crafting beautiful spaces...",
    "Elevating your interiors...",
    "Mixing colors and textures...",
    "Perfecting every detail...",
    "Arranging virtual furniture...",
    
    "Calculating perfect proportions...",
    "Adjusting lighting effects...",
    "Finalizing your new look...",
    "Polishing the final design...",
    "Making spaces flow together...",
    "Aligning with your vision...",
    
  ];

  // Check for existing session on initial load
  useEffect(() => {
    const sessionToken = localStorage.getItem('sessionToken');
    if (sessionToken) {
      // Fetch user info using the session token
      fetch(`${process.env.REACT_APP_API_URL}/check-auth`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      })
      .then(response => response.json())
      .then(data => {
        if (data.email) {
          setUser({
            email: data.email,
            sessionToken
          });
        }
      })
      .catch(console.error);
    }
  }, []);

  // Check for existing session on load
  useEffect(() => {
    const sessionToken = localStorage.getItem('sessionToken');
    if (sessionToken && !user) {
      fetch(`${process.env.REACT_APP_API_URL}/check-auth`, {
        headers: {
          'Authorization': `Bearer ${sessionToken}`
        }
      })
      .then(response => response.json())
      .then(data => {
        if (data.email) {
          setUser({
            email: data.email,
            sessionToken
          });
        } else {
          // If session is invalid, clear it
          localStorage.removeItem('sessionToken');
        }
      })
      .catch(() => {
        localStorage.removeItem('sessionToken');
      });
    }
  }, [user]);

  const onDropHome = useCallback(acceptedFiles => {
    setHomeImage(acceptedFiles[0]);
  }, []);

  const onDropInspiration = useCallback(acceptedFiles => {
    setInspirationImage(acceptedFiles[0]);
  }, []);

  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [renderProgress, setRenderProgress] = useState(null);
  const [requestId, setRequestId] = useState(null);
  const [resultImage, setResultImage] = useState(null);

  // Poll for status updates
  useEffect(() => {
    if (!requestId) return;
    
    // Make sure we have an interval running for the loading messages
    if (renderProgress === 'processing' && !loadingIntervalRef.current) {
      console.log('Status polling noticed missing interval - restarting it');
      loadingIntervalRef.current = setInterval(() => {
        setLoadingTextIndex(prevIndex => {
          const newIndex = (prevIndex + 1) % loadingMessages.length;
          console.log('Interval restarted - new message:', loadingMessages[newIndex]);
          return newIndex;
        });
      }, 1500);
    }
    
    const checkStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/status?request_id=${requestId}`, {
          headers: {
            'Authorization': `Bearer ${user.sessionToken}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to check status');
        }
        
        const data = await response.json();
        
        // Update progress based on status
        if (data.status === 'completed') {
          setRenderProgress('completed');
          // Clear the interval
          if (loadingIntervalRef.current) {
            console.log('Clearing interval - process completed');
            clearInterval(loadingIntervalRef.current);
            loadingIntervalRef.current = null;
          }
          // Show the image directly on the page
          const imageUrl = data.combined_image_url;
          console.log('Received image URL:', imageUrl);
          setResultImage(imageUrl);
          setTimeout(() => {
            setRenderProgress(null);
          }, 1000);
        } else if (data.status === 'error') {
          setRenderProgress('error');
          // Clear the interval
          if (loadingIntervalRef.current) {
            console.log('Clearing interval - process error');
            clearInterval(loadingIntervalRef.current);
            loadingIntervalRef.current = null;
          }
          setUploadError(data.error_message || 'An error occurred during processing');
          setTimeout(() => {
            setRenderProgress(null);
            setRequestId(null);
          }, 3000);
        } else {
          // Keep polling if still processing
          setTimeout(checkStatus, 2000);
        }
      } catch (error) {
        console.error('Status check error:', error);
        setTimeout(checkStatus, 5000); // Retry with longer delay on error
      }
    };
    
    if (renderProgress === 'processing') {
      checkStatus();
    }
    
    // Cleanup function
    return () => {
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
        loadingIntervalRef.current = null;
      }
    };
  }, [requestId, renderProgress, user]);

  // Clear interval when component unmounts
  useEffect(() => {
    return () => {
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
      }
    };
  }, []);

  // Debug effect to monitor loadingTextIndex changes
  useEffect(() => {
    console.log('loadingTextIndex changed to:', loadingTextIndex, 'message:', loadingMessages[loadingTextIndex]);
  }, [loadingTextIndex]);

  const handleRedesign = async () => {
    if (!homeImage || !inspirationImage) return;
    
    setIsUploading(true);
    setUploadError(null);

    // Clear any existing interval
    if (loadingIntervalRef.current) {
      clearInterval(loadingIntervalRef.current);
      loadingIntervalRef.current = null;
    }
    
    // Start cycling through loading messages immediately
    setLoadingTextIndex(0);
    console.log('Starting message cycling, initial message:', loadingMessages[0]);
    loadingIntervalRef.current = setInterval(() => {
      setLoadingTextIndex(prevIndex => {
        const newIndex = (prevIndex + 1) % loadingMessages.length;
        console.log('Cycling to next message:', loadingMessages[newIndex], 'index:', newIndex);
        return newIndex;
      });
    }, 1500);

    try {
      const formData = new FormData();
      formData.append('home_image', homeImage);
      formData.append('inspiration_image', inspirationImage);

      const response = await fetch(`${process.env.REACT_APP_API_URL}/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${user.sessionToken}`
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('Upload successful:', data);
      
      // Start render progress tracking
      setRequestId(data.request_id);
      setRenderProgress('processing');
      
      // Important: Set isUploading to false here
      console.log('Upload complete, setting isUploading to false');
      setIsUploading(false);

    } catch (error) {
      console.error('Upload error:', error);
      setUploadError(error.message);
      setRenderProgress(null);
      
      // Clear the interval if upload fails
      if (loadingIntervalRef.current) {
        clearInterval(loadingIntervalRef.current);
        loadingIntervalRef.current = null;
      }
      
      // Also set isUploading to false here
      setIsUploading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/verify-magic-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          email: verifyingEmail,
          code: verificationCode 
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Store session token in localStorage
        localStorage.setItem('sessionToken', data.session_token);
        setUser({ 
          email: data.email, 
          sessionToken: data.session_token 
        });
      } else {
        setError(data.error || 'Failed to verify code');
      }
    } catch (error) {
      setError('Error verifying code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      // Save email to localStorage for future visits
      localStorage.setItem('userEmail', email);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL}/request-magic-link`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        setVerificationSent(true);
        setVerifyingEmail(email);
      } else {
        setError(data.error || 'Failed to send verification code');
      }
    } catch (error) {
      setError('Error sending verification code');
    } finally {
      setIsLoading(false);
    }
  };

  // Render login form if not authenticated
  if (!user) {
    return (
      <Container>
        <Title>FlashReno</Title>
        <LoginForm>
          {verificationSent ? (
            <>
              <h2>Enter Verification Code</h2>
              <p>We've sent a verification code to your email.</p>
              <form onSubmit={handleVerifyCode}>
                <Input
                  type="text"
                  placeholder="Enter verification code"
                  value={verificationCode}
                  onChange={(e) => setVerificationCode(e.target.value.toUpperCase())}
                  pattern="[A-Z0-9]{7}"
                  maxLength={7}
                  style={{ textTransform: 'uppercase', letterSpacing: '0.2em' }}
                  required
                />
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? 'Verifying...' : 'Verify Code'}
                </Button>
              </form>
              <div style={{ marginTop: '1rem', fontSize: '0.9rem' }}>
                <button 
                  onClick={() => {
                    setVerificationSent(false);
                    setError(null);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#007bff',
                    textDecoration: 'underline',
                    cursor: 'pointer',
                    padding: 0,
                    font: 'inherit'
                  }}
                >
                  Back to Login
                </button>
              </div>
            </>
          ) : (
            <>
              <h2>Welcome</h2>
              <p>Enter your email to get started</p>
              <form onSubmit={handleLogin}>
                <Input
                  type="email"
                  name="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                />
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? 'Sending...' : 'Send Verification Code'}
                </Button>
              </form>
            </>
          )}
          {error && <div style={{ color: 'red', marginTop: '1rem' }}>{error}</div>}
        </LoginForm>
      </Container>
    );
  }

  return (
    <Container>
      {user && (
        <UserStatus>
          {user.email}
        </UserStatus>
      )}
      <Title>FlashReno</Title>
      
      {resultImage ? (
        <>
          <ResultContainer>
            <ResultTitle>Your Redesigned Home</ResultTitle>
            <ResultImage src={resultImage} alt="Redesigned home" />
            <p style={{ marginTop: '1.5rem' }}>Here's your space redesigned in the style of your inspiration image!</p>
            <Button 
              onClick={() => {
                setResultImage(null);
                setHomeImage(null);
                setInspirationImage(null);
                setRequestId(null);
              }}
              style={{ marginTop: '1.5rem' }}
            >
              Start Over
            </Button>
          </ResultContainer>
        </>
      ) : (
        <>
          <DropzoneContainer>
            <ImageDropzone
              onDrop={onDropHome}
              title="Your Space"
              image={homeImage}
            />
            <ImageDropzone
              onDrop={onDropInspiration}
              title="Inspiration Image"
              image={inspirationImage}
            />
          </DropzoneContainer>
          <ButtonContainer>
            <Button
              onClick={handleRedesign}
              disabled={!homeImage || !inspirationImage || isUploading || renderProgress === 'processing'}
              style={{
                position: 'relative',
                height: '60px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {!homeImage || !inspirationImage 
                ? "Add both images to continue" 
                : isUploading || renderProgress === 'processing' ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ButtonSpinner />
                    <span>
                      {(() => {
                        // Fixed logic to handle state transitions correctly
                        let displayMessage;
                        
                        if (isUploading) {
                          // Only during initial upload
                          displayMessage = 'Uploading images...';
                        } else if (renderProgress === 'processing') {
                          // During processing, use the cycling messages
                          displayMessage = loadingMessages[loadingTextIndex];
                        } else {
                          // Default fallback (shouldn't normally happen)
                          displayMessage = 'Processing...';
                        }
                        console.log('Button display message:', displayMessage, 
                          'Current index:', loadingTextIndex, 
                          'isUploading:', isUploading, 
                          'renderProgress:', renderProgress);
                        return displayMessage;
                      })()}
                    </span>
                  </div>
                ) : "Redesign My Space"}
              {uploadError && (
                <div style={{ color: 'red', marginTop: '1rem', fontSize: '0.9rem', position: 'absolute', bottom: '-30px', left: 0, right: 0 }}>
                  Error: {uploadError}
                </div>
              )}
            </Button>
          </ButtonContainer>
        </>
      )}
      
      {(renderProgress === 'processing' || isUploading) && (
        <LoadingOverlay>
          <Spinner />
          <LoadingText>
            {(() => {
              let message;
              if (isUploading && !renderProgress) {
                message = 'Uploading images...';
              } else {
                message = loadingMessages[loadingTextIndex];
              }
              console.log('LoadingOverlay message:', message, 'Current index:', loadingTextIndex);
              return message;
            })()}<br/>
            This can take up to 1 minute while our AI transforms your space.<br/>
            Please wait while the magic happens!
          </LoadingText>
        </LoadingOverlay>
      )}
    </Container>
  );
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<AppContent />} />
        <Route path="/admin" element={<AdminPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
